/**
 * Shopify Lazy Loading Image Handler
 * Optimized for Shopify themes with Liquid template support
 */

(function() {
    'use strict';

    class ShopifyLazyLoader {
        constructor(options = {}) {
            this.options = {
                rootMargin: '50px 0px',
                threshold: 0.01,
                fadeInDuration: 400,
                retryAttempts: 3,
                retryDelay: 1000,
                ...options
            };
            
            this.observer = null;
            this.loadedImages = new Set();
            this.failedImages = new Set();
            this.init();
        }

        init() {
            // Check if Intersection Observer is supported
            if (!('IntersectionObserver' in window)) {
                console.warn('Intersection Observer not supported, loading all images immediately');
                this.loadAllImages();
                return;
            }

            // Create intersection observer
            this.observer = new IntersectionObserver(
                this.handleIntersection.bind(this),
                {
                    rootMargin: this.options.rootMargin,
                    threshold: this.options.threshold
                }
            );

            // Start observing existing lazy images
            this.observeImages();
            
            // Listen for Shopify section reloads
            this.setupShopifyEventListeners();
        }

        setupShopifyEventListeners() {
            // Listen for Shopify section reloads (theme customizer)
            document.addEventListener('shopify:section:load', () => {
                setTimeout(() => this.observeImages(), 100);
            });

            // Listen for cart drawer updates
            document.addEventListener('cart:updated', () => {
                setTimeout(() => this.observeImages(), 100);
            });

            // Listen for product form updates
            document.addEventListener('variant:change', () => {
                setTimeout(() => this.observeImages(), 100);
            });
        }

        observeImages() {
            const lazyImages = document.querySelectorAll('.lazy-image:not(.loaded):not(.loading):not(.error)');
            lazyImages.forEach(img => {
                if (!this.loadedImages.has(img)) {
                    this.observer.observe(img);
                }
            });
        }

        handleIntersection(entries) {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.loadImage(entry.target);
                    this.observer.unobserve(entry.target);
                }
            });
        }

        loadImage(img, attempt = 1) {
            const container = img.closest('.lazy-image-container');
            
            // Skip if already processed
            if (this.loadedImages.has(img) || this.failedImages.has(img)) {
                return;
            }

            // Add loading class
            img.classList.add('loading');
            if (container) {
                container.classList.add('loading');
            }

            // Get image source - prioritize Shopify image URLs
            const src = this.getImageSource(img);
            
            if (!src) {
                this.handleImageError(img, container);
                return;
            }

            // Create a new image to preload
            const imageLoader = new Image();
            
            imageLoader.onload = () => {
                // Image loaded successfully
                img.src = imageLoader.src;
                img.classList.remove('loading');
                img.classList.add('loaded');
                this.loadedImages.add(img);
                
                if (container) {
                    container.classList.remove('loading');
                    container.classList.add('loaded');
                }

                // Handle srcset for responsive images
                if (img.dataset.srcset) {
                    img.srcset = img.dataset.srcset;
                }

                // Trigger custom events for Shopify analytics
                this.triggerShopifyEvent(img, 'lazyImageLoaded', { src: img.src });
            };

            imageLoader.onerror = () => {
                // Retry logic for failed images
                if (attempt < this.options.retryAttempts) {
                    setTimeout(() => {
                        this.loadImage(img, attempt + 1);
                    }, this.options.retryDelay * attempt);
                } else {
                    this.handleImageError(img, container);
                }
            };

            // Start loading the image
            imageLoader.src = src;
        }

        getImageSource(img) {
            // Priority order for Shopify images
            return img.dataset.src || 
                   img.dataset.original || 
                   img.getAttribute('data-shopify-src') ||
                   img.src;
        }

        handleImageError(img, container) {
            img.classList.remove('loading');
            img.classList.add('error');
            this.failedImages.add(img);
            
            if (container) {
                container.classList.remove('loading');
                container.classList.add('error');
            }

            // Trigger error event
            this.triggerShopifyEvent(img, 'lazyImageError', { 
                src: this.getImageSource(img) 
            });
        }

        triggerShopifyEvent(img, eventName, detail) {
            // Custom event for Shopify theme integration
            const event = new CustomEvent(eventName, {
                detail: {
                    ...detail,
                    element: img,
                    timestamp: Date.now()
                },
                bubbles: true
            });
            
            img.dispatchEvent(event);
            
            // Also trigger on document for global listeners
            document.dispatchEvent(new CustomEvent(`shopify:${eventName}`, {
                detail: {
                    ...detail,
                    element: img,
                    timestamp: Date.now()
                }
            }));
        }

        loadAllImages() {
            // Fallback for browsers without Intersection Observer
            const lazyImages = document.querySelectorAll('.lazy-image:not(.loaded):not(.error)');
            lazyImages.forEach(img => this.loadImage(img));
        }

        // Method to handle dynamic content (AJAX cart, quick view, etc.)
        refresh() {
            this.observeImages();
        }

        // Method to preload specific product images
        preloadProductImages(productHandle) {
            const productImages = document.querySelectorAll(`[data-product="${productHandle}"] .lazy-image`);
            productImages.forEach(img => {
                if (!this.loadedImages.has(img)) {
                    this.loadImage(img);
                    if (this.observer) {
                        this.observer.unobserve(img);
                    }
                }
            });
        }

        // Destroy the observer
        destroy() {
            if (this.observer) {
                this.observer.disconnect();
            }
        }
    }

    // Shopify-specific helper functions
    window.ShopifyLazyLoader = {
        // Generate Shopify image URL with size
        getShopifyImageUrl: function(src, size = '600x600') {
            if (!src) return '';
            
            // Remove existing size parameters
            const cleanSrc = src.replace(/(_\d+x\d*|_\d*x\d+|_master|_grande|_large|_medium|_small|_thumb|_compact|_cropped|_icon)/g, '');
            
            // Add new size parameter
            const extension = cleanSrc.split('.').pop();
            const baseUrl = cleanSrc.replace(`.${extension}`, '');
            
            return `${baseUrl}_${size}.${extension}`;
        },

        // Generate responsive srcset for Shopify images
        generateSrcset: function(src, sizes = ['300x300', '600x600', '900x900', '1200x1200']) {
            if (!src) return '';
            
            return sizes.map(size => {
                return `${this.getShopifyImageUrl(src, size)} ${size.split('x')[0]}w`;
            }).join(', ');
        },

        // Create lazy image HTML for Shopify
        createLazyImageHtml: function(src, alt = '', options = {}) {
            const {
                containerClass = '',
                imageClass = '',
                size = 'medium',
                responsive = true,
                productHandle = ''
            } = options;

            const srcset = responsive ? this.generateSrcset(src) : '';
            const productAttr = productHandle ? `data-product="${productHandle}"` : '';
            
            return `
                <div class="lazy-image-container ${size} ${containerClass}">
                    <img class="lazy-image ${imageClass}" 
                         data-src="${src}" 
                         ${srcset ? `data-srcset="${srcset}"` : ''}
                         alt="${alt}"
                         ${productAttr}
                         loading="lazy">
                </div>
            `;
        }
    };

    // Initialize when DOM is ready
    function initShopifyLazyLoader() {
        window.shopifyLazyLoader = new ShopifyLazyLoader({
            rootMargin: '100px 0px',
            threshold: 0.1,
            fadeInDuration: 400
        });

        // Listen for theme section changes
        const observer = new MutationObserver(function(mutations) {
            let shouldRefresh = false;
            
            mutations.forEach(function(mutation) {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                    const hasNewLazyImages = Array.from(mutation.addedNodes).some(node => {
                        return node.nodeType === 1 && (
                            node.classList?.contains('lazy-image') ||
                            node.querySelector?.('.lazy-image')
                        );
                    });
                    
                    if (hasNewLazyImages) {
                        shouldRefresh = true;
                    }
                }
            });
            
            if (shouldRefresh) {
                window.shopifyLazyLoader.refresh();
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    // Initialize based on document state
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initShopifyLazyLoader);
    } else {
        initShopifyLazyLoader();
    }

    // Expose for Shopify theme integration
    window.ShopifyLazyLoader.init = initShopifyLazyLoader;

})();
